/* eslint-disable no-restricted-globals */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/**
 * PROSPECT FIELD RENDERERS
 *
 * Handles special rendering for editable fields:
 * - Deal Value (number input)
 * - Closing Date (date picker)
 * - Prospect Overview (textarea popover)
 */

import React from 'react';
import { Calendar, ChevronDown, Check } from '@saleshandy/icons';
import DatePicker from 'react-datepicker';
import InputNumber from '../../../../../../../../shared/design-system/components/input-number';
import 'react-datepicker/dist/react-datepicker.css';
import { accessibleOnClick } from '../../../../../../../../shared/utils/accessible-on-click';
import {
  EDITABLE_FIELD_IDS,
  ProspectProfileField,
  ProspectProfileFieldContext,
} from './prospect-fields';
import {
  OverlayPopover,
  Placement,
} from '../../../../../../../../shared/design-system/components/overlay';
import { RequestStatus } from '../../../../../../../../shared/enums/request-status';
import Input from '../../../../../../../../shared/design-system/components/input/input';
import TruncatedTextWithAutoTooltip from '../../../../../../../../shared/components/truncated-text-with-auto-tooltip';
import Icon from '../../../../../../../../shared/design-system/components/atoms/icon';
import Select from '../../../../../../../../shared/design-system/components/select/select';
import { FieldType } from '../../../../../../../settings/enums/field';
import { Field } from '../../../../../../../settings/types/field';
import TruncatedTextWithTooltip from '../../../../../../../../shared/components/truncated-text-with-tooltip';

// Simple map to store cleanup functions for dropdown Escape handlers
const escapeCleanupMap = new Map<string, () => void>();

type FieldRendererParams = {
  field: ProspectProfileField;
  context: ProspectProfileFieldContext;
  editingFieldId: string | null;
  dealValue: number;
  closingDate: Date | null;
  datePickerOpen: boolean;
  onDealValueChange: (value: number) => void;
  onClosingDateChange: (date: Date | null) => void;
  onEditEnd: () => void;
  onDatePickerOpenChange: (open: boolean) => void;
  prospectOverviewValue: string;
  prospectOverviewDraft: string;
  onProspectOverviewChange: (value: string) => void;
  onProspectOverviewSave: (value: string) => void;
  onProspectOverviewCancel: () => void;
  saveContactAttributesRequestStatus: RequestStatus;
  onEditClick?: (fieldId: string) => void;
  // Generic editable text fields
  editableFieldValues?: Record<string, string>;
  onEditableFieldChange?: (fieldId: string, value: string) => void;
  onEditCancel?: () => void;
  currencySymbol: string | null; // For deal-value field (backward compatibility)
  getCurrencySymbol?: (fieldId: string) => string; // Function to get currency symbol by field ID
  getCurrencyCode?: (fieldId: string) => string | undefined; // Function to get currency code by field ID
  showDropdown: boolean;
  currencyFieldData: string | null;
};

export const getNodeText = (node: React.ReactNode): string => {
  if (node === null || node === undefined) return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(getNodeText).join('');
  if (React.isValidElement(node)) return getNodeText(node.props.children);
  return '';
};

export function renderFieldValue({
  field,
  context,
  editingFieldId,
  dealValue,
  closingDate,
  datePickerOpen,
  onDealValueChange,
  onClosingDateChange,
  onEditEnd,
  onDatePickerOpenChange,
  prospectOverviewValue,
  prospectOverviewDraft,
  onProspectOverviewChange,
  onProspectOverviewSave,
  onProspectOverviewCancel,
  onEditClick,
  editableFieldValues = {},
  onEditableFieldChange,
  onEditCancel,
  currencySymbol,
  getCurrencySymbol,
  getCurrencyCode,
  showDropdown,
  currencyFieldData,
}: FieldRendererParams): React.ReactNode {
  // Deal Value - Editable number input
  if (field.id === 'deal-value') {
    if (editingFieldId === 'deal-value') {
      // Ensure we never pass NaN to InputNumber
      // eslint-disable-next-line no-restricted-globals
      const displayValue = isNaN(dealValue) ? 0 : dealValue;
      return (
        <div className="d-flex justify-content-between align-items-center w-100 position-relative">
          <InputNumber
            value={displayValue}
            onChange={(value) => {
              if (value === null) return;
              // limit length
              if (value?.length > 15) {
                value = value?.slice(0, 15);
              }
              // Only update if value is empty or a valid number
              // eslint-disable-next-line no-restricted-globals
              if (value === '' || !isNaN(Number(value))) {
                onDealValueChange(value === '' ? 0 : Number(value));
              }
            }}
            onBlur={onEditCancel}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onEditEnd();
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                onEditCancel?.();
              }
            }}
            autoFocus
            className="prospect-profile-edit-input"
            showPrefix
            currencyCode={currencyFieldData}
          />

          <div
            className={`prospect-profile-field-check-icon ${
              editingFieldId === 'deal-value' ? 'check-icon-deal-value' : ''
            }`}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onEditEnd();
            }}
          >
            <Check width={16} height={16} />
          </div>
        </div>
      );
    }
    return (
      <span>
        {dealValue ? (
          <>
            {currencySymbol}
            {dealValue.toLocaleString()}
          </>
        ) : (
          <span
            className="gray-txt-11 editable-field-click-to-add"
            {...accessibleOnClick(() => {
              onEditClick?.(field.id);
            })}
          >
            Click to add
          </span>
        )}
      </span>
    );
  }

  // Prospect Overview - Editable textarea popover
  if (field.id === 'prospect-overview') {
    const rawOverviewValue =
      getNodeText(field.value ?? field.searchValue ?? '').trim() ||
      prospectOverviewValue;
    // Filter out hyphen "-" which is used for display purposes only
    const overviewValue =
      rawOverviewValue && rawOverviewValue !== '-' ? rawOverviewValue : '';

    if (editingFieldId === 'prospect-overview') {
      return (
        <OverlayPopover
          show
          placement={Placement.Top}
          className="prospect-overview-popover"
          content={
            <div className="prospect-overview-popover-content">
              <span className="prospect-overview-header">
                Prospect Overview
              </span>
              <textarea
                value={prospectOverviewDraft}
                onChange={(event) =>
                  onProspectOverviewChange(event.target.value)
                }
                className={`prospect-overview-textarea ${
                  prospectOverviewDraft?.length > 10000
                    ? 'longtext-textarea-error'
                    : ''
                }`}
                rows={4}
                autoFocus
              />
              {prospectOverviewDraft?.length > 10000 && (
                <span className="error-text mt-0">
                  Characters limit exceeds 10000
                </span>
              )}
              <div className="prospect-overview-popover-actions">
                <button
                  type="button"
                  className="prospect-overview-popover-action-close"
                  onClick={onProspectOverviewCancel}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={`prospect-overview-popover-action-save ${
                    prospectOverviewDraft?.length > 10000 ? 'disabled' : ''
                  }`}
                  onClick={() => onProspectOverviewSave(prospectOverviewDraft)}
                  disabled={prospectOverviewDraft?.length > 10000}
                >
                  Save
                </button>
              </div>
            </div>
          }
          rootClose
          onToggle={(nextShow: boolean) => {
            if (!nextShow) {
              onProspectOverviewCancel();
            }
          }}
        >
          <span className="prospect-overview-value">
            {overviewValue || 'Add overview'}
          </span>
        </OverlayPopover>
      );
    }

    return overviewValue ? (
      <TruncatedTextWithAutoTooltip
        text={overviewValue}
        className="editable-field"
        placement={Placement.Top}
      />
    ) : (
      field.value || <span className="gray-txt-12">-</span>
    );
  }

  // Closing Date - Editable date picker
  if (field.id === 'closing-date') {
    if (editingFieldId === 'closing-date') {
      const DateInput = React.forwardRef<
        HTMLInputElement,
        React.HTMLProps<HTMLInputElement> & {
          onClear?: () => void;
        }
      >(({ value, onClick, onChange, onClear }, ref) => (
        <div className="bs-input date-picker-container">
          <Calendar />
          <input
            placeholder="Select Date"
            onClick={onClick}
            className="input bs-input"
            style={{ textIndent: 0 }}
            onChange={onChange}
            value={value ? value.toString() : ''}
            ref={ref}
            readOnly // important to avoid manual typing issues
          />

          <div className="d-flex align-items-center">
            {onClear && value && (
              <Icon
                className="cal-icon mr-1 sh-close close-icon"
                identifier="close"
                onClick={(e) => {
                  e.stopPropagation();
                  onClear?.();
                }}
              />
            )}
          </div>
        </div>
      ));

      return (
        <div
          className="d-flex justify-content-between align-items-center w-100 position-relative"
          ref={(node) => {
            // Cleanup previous listener for this field
            const existingCleanup = escapeCleanupMap.get(field.id);
            if (existingCleanup) {
              existingCleanup();
              escapeCleanupMap.delete(field.id);
            }

            if (node && editingFieldId === field.id && onEditEnd) {
              const handleEscape = (e: KeyboardEvent) => {
                if (e.key === 'Escape' && editingFieldId === field.id) {
                  e.preventDefault();
                  e.stopPropagation();
                  onEditEnd();
                }
              };

              // Add listener to catch Escape even when DatePicker is open
              document.addEventListener('keydown', handleEscape, true);

              // Store cleanup function
              const cleanup = () => {
                document.removeEventListener('keydown', handleEscape, true);
                escapeCleanupMap.delete(field.id);
              };
              escapeCleanupMap.set(field.id, cleanup);
            } else if (!node) {
              // Cleanup when unmounting
              const cleanup = escapeCleanupMap.get(field.id);
              if (cleanup) {
                cleanup();
              }
            }
          }}
        >
          <DatePicker
            selected={closingDate}
            onChange={(date: Date | null) => {
              onClosingDateChange(date);
              onDatePickerOpenChange(false);
              onEditEnd();
            }}
            onClickOutside={() => {
              onDatePickerOpenChange(false);
              onEditEnd();
            }}
            onInputClick={() => onDatePickerOpenChange(true)}
            open={datePickerOpen}
            dateFormat="MMM d, yyyy"
            customInput={
              <DateInput
                onClear={() => {
                  onClosingDateChange(null);
                  onDatePickerOpenChange(false);
                  onEditEnd();
                }}
              />
            }
            className="prospect-profile-date-picker"
          />
        </div>
      );
    }

    const isValidDate =
      // eslint-disable-next-line no-restricted-globals
      closingDate && !isNaN(closingDate.getTime());
    const formattedDate = isValidDate
      ? closingDate.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
      : null;
    return (
      <span>
        {isValidDate && formattedDate ? (
          <>
            <Calendar width={16} height={16} className="mr-1" /> {formattedDate}
          </>
        ) : (
          <span
            className="gray-txt-11"
            {...accessibleOnClick(() => {
              if (onEditClick) {
                onEditClick(field.id);
              }
            })}
          >
            Click to add
          </span>
        )}
      </span>
    );
  }

  // Handle custom fields (fields from API with different types: dropdown, currency, etc.)
  const customField = (field as any)?.customFieldData as Field | undefined;
  if (customField && field.showEdit) {
    const { fieldType } = customField;
    const hasEditedValue = field?.id in (editableFieldValues || {});
    let fieldValue: string;

    if (hasEditedValue) {
      fieldValue = editableFieldValues?.[field.id] || '';
    } else if (editingFieldId === field.id) {
      fieldValue = '';
    } else {
      fieldValue = getNodeText(field.value ?? '').trim();
    }

    // Handle editing mode for custom fields
    if (editingFieldId === field.id) {
      // Dropdown field type
      if (fieldType === FieldType.Dropdown) {
        const rawOptions =
          customField?.fieldMetadata?.metadata?.options ||
          customField?.options ||
          [];
        const dropdownOptions = rawOptions.map((option: any) => {
          // Handle both string and ExtendedOption types
          if (typeof option === 'string') {
            return { key: option, label: option };
          }
          return {
            key: option.key || option.value,
            label: option.label || option.value,
          };
        });

        return (
          showDropdown && (
            <div
              className="d-flex justify-content-between align-items-center w-100 position-relative"
              ref={(node) => {
                // Cleanup previous listener for this field
                const existingCleanup = escapeCleanupMap.get(field.id);
                if (existingCleanup) {
                  existingCleanup();
                  escapeCleanupMap.delete(field.id);
                }

                if (node && onEditCancel) {
                  const handleEscape = (e: KeyboardEvent) => {
                    if (e.key === 'Escape' && editingFieldId === field.id) {
                      e.preventDefault();
                      e.stopPropagation();
                      onEditCancel();
                    }
                  };

                  // Add listener to catch Escape even when Select dropdown is open
                  document.addEventListener('keydown', handleEscape, true);

                  // Store cleanup function
                  const cleanup = () => {
                    document.removeEventListener('keydown', handleEscape, true);
                    escapeCleanupMap.delete(field.id);
                  };
                  escapeCleanupMap.set(field.id, cleanup);
                } else if (!node) {
                  // Cleanup when unmounting
                  const cleanup = escapeCleanupMap.get(field.id);
                  if (cleanup) {
                    cleanup();
                  }
                }
              }}
            >
              <Select
                options={dropdownOptions}
                selectedOptionKey={fieldValue || null}
                optionRenderer={(option) => (
                  <TruncatedTextWithAutoTooltip
                    text={option?.label || ''}
                    className="dropdown-value-option"
                  />
                )}
                selectedOptionRenderer={([option]) => (
                  <TruncatedTextWithAutoTooltip
                    text={option?.label || ''}
                    className="dropdown-value"
                  />
                )}
                onChange={([option]) => {
                  if (option && onEditableFieldChange) {
                    onEditableFieldChange(field.id, String(option.key));
                  }
                }}
                placement={Placement.Auto}
                className="prospect-profile-edit-select"
                placeholder={`Select ${field.label?.toLowerCase() || 'value'}`}
                showSearch
                filterOption={(value, option) =>
                  option?.label?.toLowerCase().includes(value.toLowerCase())
                }
                rootClose
                popperConfig={{
                  modifiers: [
                    {
                      name: 'flip',
                      options: {
                        fallbackPlacements: ['top'],
                        allowedAutoPlacements: ['bottom'],
                      },
                    },
                  ],
                }}
              />
              <div
                className="prospect-profile-field-check-icon-dropdown"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onEditEnd();
                }}
              >
                <Check width={16} height={16} />
              </div>
            </div>
          )
        );
      }

      // Currency field type
      if (fieldType === FieldType.Currency) {
        const fieldCurrencyCode = getCurrencyCode
          ? getCurrencyCode(field.id)
          : undefined;
        // Convert to number only if valid, otherwise use 0 to prevent NaN
        const numValue = fieldValue ? Number(fieldValue) : 0;
        const currencyValue = isNaN(numValue) ? 0 : numValue;
        return (
          <div className="d-flex justify-content-between align-items-center w-100 position-relative">
            <InputNumber
              value={currencyValue}
              onChange={(value) => {
                if (value === null) return;
                // limit length
                if (value?.length > 15) {
                  value = value?.slice(0, 15);
                }
                // Only update if value is empty or a valid number
                // eslint-disable-next-line no-restricted-globals
                if (value === '' || !isNaN(Number(value))) {
                  if (onEditableFieldChange) {
                    onEditableFieldChange(field.id, value === '' ? '0' : value);
                  }
                }
              }}
              onBlur={onEditCancel}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  onEditEnd();
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  onEditCancel?.();
                }
              }}
              autoFocus
              className="prospect-profile-edit-input"
              showPrefix
              currencyCode={fieldCurrencyCode}
            />
            <div
              className="prospect-profile-field-check-icon-currency"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onEditEnd();
              }}
            >
              <Check width={16} height={16} />
            </div>
          </div>
        );
      }

      // Number field type
      if (fieldType === FieldType.Number) {
        // Convert to number only if valid, otherwise use 0 to prevent NaN
        const numValue = fieldValue ? Number(fieldValue) : 0;
        const numberValue = isNaN(numValue) ? 0 : numValue;
        return (
          <div className="d-flex justify-content-between align-items-center w-100 position-relative">
            <InputNumber
              value={numberValue}
              onChange={(value) => {
                if (value === null) return;
                // Only update if value is empty or a valid number
                // eslint-disable-next-line no-restricted-globals
                if (value === '' || !isNaN(Number(value))) {
                  if (onEditableFieldChange) {
                    onEditableFieldChange(field.id, value === '' ? '0' : value);
                  }
                }
              }}
              onBlur={onEditCancel}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  onEditEnd();
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  onEditCancel?.();
                }
              }}
              autoFocus
              className="prospect-profile-edit-input"
            />
            <div
              className="prospect-profile-field-check-icon-currency"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onEditEnd();
              }}
            >
              <Check width={16} height={16} />
            </div>
          </div>
        );
      }

      // Date field type
      if (fieldType === FieldType.Date) {
        const dateValue = fieldValue ? new Date(fieldValue) : null;
        const DateInput = React.forwardRef<
          HTMLInputElement,
          React.HTMLProps<HTMLInputElement> & {
            onClear?: () => void;
            onEditEnd?: () => void;
          }
        >(({ value, onClick, onClear, onEditEnd: handleEditEnd }, ref) => (
          <div
            className="bs-input date-picker-container"
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                handleEditEnd?.(); // close date picker on ESC
              }
            }}
          >
            <Calendar />
            <input
              placeholder="Select Date"
              onClick={onClick}
              className="input bs-input"
              style={{ textIndent: 0 }}
              value={value ? value.toString() : ''}
              ref={ref}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.preventDefault();
                  e.stopPropagation();
                  handleEditEnd?.(); // close date picker on ESC
                }
              }}
              readOnly // important to avoid manual typing issues
            />

            <div className="d-flex align-items-center">
              {onClear && value && (
                <Icon
                  className="cal-icon mr-1 sh-close close-icon"
                  identifier="close"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClear?.();
                  }}
                />
              )}
            </div>
          </div>
        ));
        return (
          <div
            className="d-flex justify-content-between align-items-center w-100 position-relative"
            ref={(node) => {
              // Cleanup previous listener for this field
              const existingCleanup = escapeCleanupMap.get(field.id);
              if (existingCleanup) {
                existingCleanup();
                escapeCleanupMap.delete(field.id);
              }

              if (node && editingFieldId === field.id && onEditEnd) {
                const handleEscape = (e: KeyboardEvent) => {
                  if (e.key === 'Escape' && editingFieldId === field.id) {
                    e.preventDefault();
                    e.stopPropagation();
                    onEditEnd();
                  }
                };

                // Add listener to catch Escape even when DatePicker is open
                document.addEventListener('keydown', handleEscape, true);

                // Store cleanup function
                const cleanup = () => {
                  document.removeEventListener('keydown', handleEscape, true);
                  escapeCleanupMap.delete(field.id);
                };
                escapeCleanupMap.set(field.id, cleanup);
              } else if (!node) {
                // Cleanup when unmounting
                const cleanup = escapeCleanupMap.get(field.id);
                if (cleanup) {
                  cleanup();
                }
              }
            }}
          >
            <DatePicker
              selected={dateValue}
              onChange={(date: Date | null) => {
                if (onEditableFieldChange) {
                  const dateString = date
                    ? date.toLocaleDateString('en-CA')
                    : '';
                  onEditableFieldChange(field.id, dateString);
                }
              }}
              onClickOutside={() => {
                onEditCancel(); // close on outside click
              }}
              dateFormat="MMM d, yyyy"
              placeholderText="Select Date"
              className="prospect-profile-edit-input"
              customInput={
                <DateInput
                  onClear={() => {
                    if (onEditableFieldChange) {
                      onEditableFieldChange(field.id, '');
                    }
                    onEditEnd();
                  }}
                  onEditEnd={onEditEnd}
                />
              }
              open={editingFieldId === field.id} // 🔑 force close when editing ends
            />
            <div
              className="prospect-profile-field-check-date-icon"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (fieldValue?.length > 1000) return;
                onEditEnd();
              }}
            >
              <Check width={16} height={16} />
            </div>
          </div>
        );
      }

      // LongText field type
      if (fieldType === FieldType.LongText) {
        const longTextValue =
          getNodeText(field.value ?? field.searchValue ?? '').trim() ||
          prospectOverviewValue;
        // Filter out hyphen "-" which is used for display purposes only
        const displayValue =
          longTextValue &&
          longTextValue !== '-' &&
          longTextValue !== 'Click to Add' &&
          longTextValue !== 'Click to add'
            ? longTextValue
            : '';
        return (
          <OverlayPopover
            show
            placement={Placement.Top}
            className="long-text-popover"
            content={
              <div
                className="long-text-popover-content"
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    onEditCancel?.();
                  }
                }}
              >
                <span className="long-text-header">{field.label}</span>
                <textarea
                  value={fieldValue}
                  onChange={(event) =>
                    onEditableFieldChange(field.id, event.target.value)
                  }
                  className={`long-text-textarea ${
                    fieldValue?.length > 10000 ? 'longtext-textarea-error' : ''
                  }`}
                  rows={4}
                  autoFocus
                />
                {fieldValue?.length > 10000 && (
                  <span className="error-text mt-0">
                    Characters limit exceeds 10000
                  </span>
                )}
                <div className="long-text-popover-actions">
                  <button
                    type="button"
                    className="long-text-popover-action-close"
                    onClick={onProspectOverviewCancel}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={`long-text-popover-action-save ${
                      fieldValue?.length > 10000 ? 'disabled' : ''
                    }`}
                    onClick={() => onEditEnd()}
                    disabled={fieldValue?.length > 10000}
                  >
                    Save
                  </button>
                </div>
              </div>
            }
            rootClose
            onToggle={(nextShow: boolean) => {
              if (!nextShow) {
                onEditCancel();
              }
            }}
          >
            <span className="long-text-value">
              {displayValue || 'Add value'}
            </span>
          </OverlayPopover>
        );
      }

      // Default: Text field type
      return (
        <>
          <div className="d-flex justify-content-between align-items-center w-100 position-relative">
            <Input
              value={fieldValue}
              onChange={(value?: string) => {
                if (onEditableFieldChange) {
                  onEditableFieldChange(field.id, value ?? '');
                }
              }}
              onBlur={onEditCancel}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  onEditEnd();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  if (onEditCancel) {
                    onEditCancel();
                  } else {
                    onEditEnd();
                  }
                }
              }}
              autoFocus
              className={`prospect-profile-edit-input ${
                fieldValue?.length > 1000 ? 'input-error' : ''
              }`}
              placeholder={`Enter ${field.label?.toLowerCase() || 'value'}`}
            />

            <div
              className="prospect-profile-field-check-icon"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (fieldValue?.length > 1000) return;
                onEditEnd();
              }}
            >
              <Check width={16} height={16} />
            </div>
          </div>

          {fieldValue?.length > 1000 && (
            <span className="error-text mt-0">
              Characters limit exceeds 1000
            </span>
          )}
        </>
      );
    }

    // Display mode for custom fields
    if (
      fieldValue &&
      fieldValue !== '-' &&
      fieldValue !== 'Click to Add' &&
      fieldValue !== 'Click to add'
    ) {
      // Currency display with symbol - use field-specific currency symbol
      if (fieldType === FieldType.Currency) {
        const numValue = Number(fieldValue);
        const fieldCurrencySymbol = getCurrencySymbol
          ? getCurrencySymbol(field.id)
          : currencySymbol || '$';
        return numValue ? (
          <span>
            {fieldCurrencySymbol}
            {numValue.toLocaleString()}
          </span>
        ) : (
          <span
            className="gray-txt-11 cursor-pointer editable-field-click-to-add"
            {...accessibleOnClick(() => {
              if (onEditClick) {
                onEditClick(field.id);
              }
            })}
          >
            Click to add
          </span>
        );
      }
      // Dropdown display - show selected option label
      if (fieldType === FieldType.Dropdown) {
        return (
          <TruncatedTextWithAutoTooltip
            text={fieldValue}
            className="editable-field"
          />
        );
      }
      // Date display - show calendar icon with formatted date
      if (fieldType === FieldType.Date) {
        const dateValue = fieldValue ? new Date(fieldValue) : null;
        const isValidDate =
          // eslint-disable-next-line no-restricted-globals
          dateValue && !isNaN(dateValue.getTime());
        const formattedDate = isValidDate
          ? dateValue.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
          : null;
        return (
          <span>
            {isValidDate && formattedDate ? (
              <>
                <Calendar width={16} height={16} className="mr-1" />{' '}
                {formattedDate}
              </>
            ) : (
              '-'
            )}
          </span>
        );
      }
      // Default display
      return (
        <TruncatedTextWithAutoTooltip
          text={fieldValue}
          placement={Placement.Top}
          className="editable-field"
        />
      );
    }

    // Show clickable "Click To Add" text if no value
    return (
      <span
        className="gray-txt-11 cursor-pointer editable-field-click-to-add"
        {...accessibleOnClick(() => {
          if (onEditClick) {
            onEditClick(field.id);
          }
        })}
      >
        {fieldValue === '-' ? 'Click to Add' : fieldValue || 'Click to Add'}
      </span>
    );
  }

  // Generic editable text fields (Company, Job Title, Department, Company Industry, City, State, Country)

  if (EDITABLE_FIELD_IDS?.includes(field?.id)) {
    // Check if the field has been edited (key exists in editableFieldValues)
    // Use empty string if explicitly set, otherwise fall back to original value
    const hasEditedValue = field?.id in editableFieldValues;

    // Optimize: Only call expensive getNodeText when necessary
    // If field is being edited, we already have the value in editableFieldValues
    // If field is not being edited and not in editableFieldValues, use searchValue (string) if available
    let fieldValue: string;
    if (hasEditedValue) {
      fieldValue = editableFieldValues[field.id];
    } else if (editingFieldId === field.id) {
      // Field is being edited but no value yet - use empty string
      fieldValue = '';
    } else {
      // Not editing and no edited value - try to use searchValue first (faster than getNodeText)
      // searchValue is already a string, so no need for expensive getNodeText
      const { searchValue } = field;
      if (searchValue && typeof searchValue === 'string') {
        // Extract just the value part (searchValue format: "fieldId value")
        const parts = searchValue.split(' ');
        parts.shift(); // Remove fieldId
        fieldValue = parts.join(' ').trim();
      } else {
        // Fallback to getNodeText only when absolutely necessary
        fieldValue = getNodeText(field.value ?? '').trim();
      }
    }

    if (editingFieldId === field.id) {
      return (
        <>
          <div className="d-flex justify-content-between align-items-center w-100 position-relative">
            <Input
              value={fieldValue}
              onChange={(value?: string) => {
                if (onEditableFieldChange) {
                  // Allow empty strings to be set
                  onEditableFieldChange(field.id, value ?? '');
                }
              }}
              onBlur={onEditCancel}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  onEditEnd();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  if (onEditCancel) {
                    onEditCancel();
                  } else {
                    onEditEnd();
                  }
                }
              }}
              autoFocus
              className={`prospect-profile-edit-input ${
                fieldValue?.length > 1000 ? 'input-error' : ''
              }`}
              placeholder={`Enter ${field.label?.toLowerCase() || 'value'}`}
            />
            <div
              className="prospect-profile-field-check-icon"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onEditEnd();
              }}
            >
              <Check width={16} height={16} />
            </div>
          </div>
          {fieldValue?.length > 1000 && (
            <span className="error-text mt-0">
              Characters limit exceeds 1000
            </span>
          )}
        </>
      );
    }

    // Display mode
    if (
      fieldValue &&
      fieldValue !== '-' &&
      fieldValue !== 'Click to Add' &&
      fieldValue !== 'Click to add'
    ) {
      // Special rendering for company-website: render as clickable URL
      if (
        ['company-website', 'linkedIn', 'facebook', 'twitter']?.includes(
          field?.id,
        )
      ) {
        return (
          <span>
            <a
              href={
                String(fieldValue).startsWith('http')
                  ? String(fieldValue)
                  : `https://${fieldValue}`
              }
              target="_blank"
              rel="noopener noreferrer"
            >
              {fieldValue}
            </a>
          </span>
        );
      }
      return (
        <TruncatedTextWithAutoTooltip
          text={fieldValue}
          className="editable-field"
        />
      );
    }

    // Show clickable "Click To Add" text if no value
    return (
      <span
        className="gray-txt-11 cursor-pointer editable-field-click-to-add"
        {...accessibleOnClick(() => {
          if (onEditClick) {
            onEditClick(field.id);
          }
        })}
      >
        {fieldValue === '-' ? 'Click to Add' : fieldValue || 'Click to Add'}
      </span>
    );
  }

  // Default: use renderValue if available, otherwise use value
  return field.renderValue ? field.renderValue(context) : field.value;
}
